import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ShareOptInDialog } from "@/components/ShareOptInDialog";
import { CtaPlacement } from "@/components/CtaPlacement";
import { MetricCard } from "@/components/ui/metric-card";
import { ExecutiveSummary } from "@/components/report/ExecutiveSummary";
import { PlatformLeaderboard } from "@/components/report/PlatformLeaderboard";
import { SectionHeader } from "@/components/report/SectionHeader";
import { MobileSection } from "@/components/report/MobileSection";
import { EcommerceSection } from "@/components/report/EcommerceSection";
import { LandingPageTable } from "@/components/report/LandingPageTable";
import { CountryTable } from "@/components/report/CountryTable";
import { ChoroplethSkeleton } from "@/components/report/ChoroplethSkeleton";
import { TimeSeriesSection } from "@/components/report/TimeSeriesSection";
import { SourceMixSection } from "@/components/report/SourceMixSection";
import { DepthSection } from "@/components/report/DepthSection";
import { InsightsSection } from "@/components/report/InsightsSection";
import { GranularityControl, type Granularity } from "@/components/report/GranularityControl";
import { inferGranularity } from "@/components/report/granularity";
import {
  DateRangeSelector,
  type CompareMode,
  type DateRangeValue,
} from "@/components/report/DateRangeSelector";
import {
  averageOrderValueInfo,
  customConversionsInfo,
  engagementRateInfo,
  keyEventsInfo,
  pagesPerSessionInfo,
  revenueInfo,
  sessionsInfo,
  transactionsInfo,
} from "@/components/report/metric-info";
import {
  EmptySocial,
  LoadingReport,
  ReportError as ReportErrorState,
} from "@/components/report/ReportStates";
import { rangeSubtitle } from "@shared/report/date-range";
import { buildExportsHref } from "./exports-href";
import {
  fetchBootstrap,
  fetchReport,
  type BootstrapResponse,
  type ReportCompare,
  type ReportPayload,
} from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";

// First chunk-split component in the codebase — keeps d3-geo, visx/geo and
// the world-atlas TopoJSON (~180 kB combined) out of the initial report bundle.
const WorldChoropleth = lazy(() => import("@/components/report/WorldChoropleth"));

type ReportStatus =
  | { kind: "loading" }
  | { kind: "unauthenticated" }
  | { kind: "no-property" }
  | { kind: "ready"; bootstrap: BootstrapResponse; report: ReportPayload }
  | { kind: "error"; bootstrap: BootstrapResponse | null; message: string };

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// M12.12 — full set matches the DateRangeSelector: previous /
// year_over_year / custom / none. "none" hides delta chips across the
// dashboard; the backend already tolerated it for legacy share
// envelopes, so the full round-trip is safe.
const VALID_COMPARES_UI: ReadonlyArray<CompareMode> = [
  "previous",
  "year_over_year",
  "custom",
  "none",
];

function isIsoDate(v: string | null): v is string {
  return v !== null && ISO_DATE_RE.test(v);
}

function isCompareMode(v: string | null): v is CompareMode {
  return v !== null && (VALID_COMPARES_UI as readonly string[]).includes(v);
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Default window: last 28 days ending yesterday in the browser's local
 * clock. Matches what boundsForPreset("last_28d") resolves to server-side
 * for a UTC property — small drift around midnight UTC is acceptable.
 */
function default28d(): { startDate: string; endDate: string } {
  const today = new Date();
  const end = new Date(today);
  end.setDate(today.getDate() - 1);
  const start = new Date(end);
  start.setDate(end.getDate() - 27);
  return { startDate: isoDate(start), endDate: isoDate(end) };
}

export function ReportOverview() {
  const [params, setParams] = useSearchParams();

  const fallback = useMemo(() => default28d(), []);
  const startDate = isIsoDate(params.get("start"))
    ? (params.get("start") as string)
    : fallback.startDate;
  const endDate = isIsoDate(params.get("end")) ? (params.get("end") as string) : fallback.endDate;
  const compare: CompareMode = isCompareMode(params.get("compare"))
    ? (params.get("compare") as CompareMode)
    : "previous";
  // M12.12 — compareStart/compareEnd are only meaningful when
  // compare === "custom". Drop them silently when the URL is malformed
  // instead of throwing, so old bookmarks keep resolving.
  const compareStart =
    compare === "custom" && isIsoDate(params.get("compareStart"))
      ? (params.get("compareStart") as string)
      : undefined;
  const compareEnd =
    compare === "custom" && isIsoDate(params.get("compareEnd"))
      ? (params.get("compareEnd") as string)
      : undefined;

  const [status, setStatus] = useState<ReportStatus>({ kind: "loading" });
  const [shareOpen, setShareOpen] = useState(false);

  // Hook has to run on every render, before any early-return. Picks up
  // the property name once the report lands and falls back to a generic
  // label while loading.
  const propertyName =
    status.kind === "ready"
      ? status.bootstrap.defaultProperty?.propertyName
      : status.kind === "error"
        ? status.bootstrap?.defaultProperty?.propertyName
        : undefined;
  usePageMeta({
    title: propertyName
      ? `Traffic report — ${propertyName} · Traffic Snapshot`
      : "Traffic report · Traffic Snapshot",
  });

  useEffect(() => {
    let cancelled = false;
    setStatus({ kind: "loading" });

    (async () => {
      let bootstrap: BootstrapResponse | null = null;
      try {
        bootstrap = await fetchBootstrap();
        if (cancelled) return;
        // Treat session-expired separately from no-property: the latter
        // routes the user to the picker; the former needs a re-auth so
        // they don't see "pick a property" when their cookie just lapsed.
        if (!bootstrap.authenticated) {
          setStatus({ kind: "unauthenticated" });
          return;
        }
        if (!bootstrap.defaultProperty) {
          setStatus({ kind: "no-property" });
          return;
        }
        const report = await fetchReport({
          propertyId: bootstrap.defaultProperty.propertyId,
          startDate,
          endDate,
          compare,
          ...(compare === "custom" && compareStart && compareEnd
            ? { compareStart, compareEnd }
            : {}),
        });
        if (cancelled) return;
        setStatus({ kind: "ready", bootstrap, report });
      } catch (e) {
        if (cancelled) return;
        setStatus({ kind: "error", bootstrap, message: (e as Error).message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, compare, compareStart, compareEnd]);

  const handleRangeChange = useCallback(
    (next: DateRangeValue) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set("start", next.startDate);
      nextParams.set("end", next.endDate);
      nextParams.set("compare", next.compare);
      // Drop the legacy preset param once the user picks via bounds;
      // keeping it around would confuse anyone reading the URL.
      nextParams.delete("range");
      // M12.12 — compareStart/End are meaningful only in custom mode.
      // Strip them when the user switches back to previous/YoY so the
      // URL doesn't carry orphaned state.
      if (next.compare === "custom" && next.compareStart && next.compareEnd) {
        nextParams.set("compareStart", next.compareStart);
        nextParams.set("compareEnd", next.compareEnd);
      } else {
        nextParams.delete("compareStart");
        nextParams.delete("compareEnd");
      }
      setParams(nextParams, { replace: true });
    },
    [params, setParams],
  );

  // M13.1 — page-level granularity. Smart default derived from the
  // selected window in days (≤30 = day, 31-120 = week, >120 = month);
  // user can override via <GranularityControl> next to the section
  // header. Re-derives whenever the date-range changes so picking a new
  // window resets to the new smart default unless the user has
  // explicitly chosen something. Hooks live above the early `<Navigate>`
  // return so React's hooks rules stay happy across status transitions.
  const defaultGranularity = useMemo<Granularity>(
    () => inferGranularity(startDate, endDate),
    [startDate, endDate],
  );
  const [granularityOverride, setGranularityOverride] = useState<Granularity | null>(null);
  const granularity: Granularity = granularityOverride ?? defaultGranularity;
  // Reset the override when the window changes — picking a new range
  // should fall back to the smart default unless the user re-overrides.
  useEffect(() => {
    setGranularityOverride(null);
  }, [defaultGranularity]);

  if (status.kind === "unauthenticated") {
    return <Navigate to="/app/connect?return_to=%2Fapp%2Freport" replace />;
  }
  // After the early return above, the status is narrowed for renderBody.
  const renderable: RenderableStatus = status;

  const property =
    renderable.kind === "ready"
      ? renderable.bootstrap.defaultProperty
      : renderable.kind === "error"
        ? (renderable.bootstrap?.defaultProperty ?? null)
        : null;
  const env = renderable.kind === "ready" ? renderable.bootstrap.env : undefined;
  const propertyIdForShare = renderable.kind === "ready" ? renderable.report.meta.propertyId : null;
  const comparePeriodStart =
    renderable.kind === "ready" ? renderable.report.meta.comparePeriodStart : null;
  const subtitle = rangeSubtitle(startDate, endDate, compare, comparePeriodStart);

  return (
    <AppShell
      property={property ?? null}
      eyebrow="Traffic report"
      appEnv={env}
      title={
        <>
          Social traffic <em className="font-serif text-purple-700">at a glance</em>
        </>
      }
      subtitle={subtitle}
      actions={
        propertyIdForShare ? (
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="ghost">
              <Link
                to={buildExportsHref({ startDate, endDate, compare, compareStart, compareEnd })}
              >
                <i className="fa-regular fa-file-export text-sm" aria-hidden="true" />
                Exports
              </Link>
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShareOpen(true)}>
              <i className="fa-regular fa-share-nodes text-sm" aria-hidden="true" />
              Share report
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="space-y-6">
        <DateRangeSelector
          value={{
            startDate,
            endDate,
            compare,
            ...(compareStart ? { compareStart } : {}),
            ...(compareEnd ? { compareEnd } : {}),
          }}
          onChange={handleRangeChange}
        />
        {/* M14 — InsightsSection lives at the top level (between
            DateRangeSelector and the report body) so it (a) renders
            "directly under Traffic report" per design intent and (b)
            fires its /api/insights fetch in parallel with /api/report
            instead of waiting for the report body to mount. */}
        {propertyIdForShare ? (
          <InsightsSection
            propertyId={propertyIdForShare}
            startDate={startDate}
            endDate={endDate}
            compare={compare}
            {...(compare === "custom" && compareStart ? { compareStart } : {})}
            {...(compare === "custom" && compareEnd ? { compareEnd } : {})}
          />
        ) : null}
        {renderBody(renderable, { granularity, onGranularityChange: setGranularityOverride })}
      </div>
      {propertyIdForShare ? (
        <ShareOptInDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          propertyId={propertyIdForShare}
          startDate={startDate}
          endDate={endDate}
          compare={compare}
          {...(compare === "custom" && compareStart && compareEnd
            ? { compareStart, compareEnd }
            : {})}
        />
      ) : null}
    </AppShell>
  );
}

type RenderableStatus = Exclude<ReportStatus, { kind: "unauthenticated" }>;

interface RenderBodyOptions {
  granularity: Granularity;
  onGranularityChange: (next: Granularity) => void;
}

function renderBody(status: RenderableStatus, opts: RenderBodyOptions) {
  if (status.kind === "no-property") {
    return (
      <div className="space-y-6">
        <Alert>
          <i className="fa-regular fa-circle-info text-sm" aria-hidden="true" />
          <AlertTitle>Pick a property to see the report</AlertTitle>
          <AlertDescription>
            Connect a GA4 property and we'll render this view against the last 28 days of social
            sessions.
          </AlertDescription>
        </Alert>
        <Button asChild>
          <Link to="/app/properties">
            Choose property
            <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
          </Link>
        </Button>
      </div>
    );
  }

  if (status.kind === "error") {
    return <ReportErrorState message={status.message} />;
  }

  if (status.kind === "loading") {
    return <LoadingReport />;
  }

  const { overview, perPlatform, perCountry, landingPages, recommendations, meta } = status.report;

  if (overview.sessions === 0) {
    return (
      <div className="space-y-8">
        <EmptySocial />
        <CtaPlacement
          context={{
            route: "/app/report",
            signals: { isEmptyState: true },
          }}
        />
      </div>
    );
  }

  const showRevenue = overview.totalRevenue > 0;
  const propertyLabel = status.bootstrap.defaultProperty?.propertyName ?? "report";
  const digestSubscribedForProperty =
    status.bootstrap.digestSubscriptions?.some((s) => s.propertyId === meta.propertyId) ?? false;

  // M13.4 — gate the new ecommerce surfaces on probe-pass + windowActive.
  // Probe-pass alone isn't enough: a property that did ecommerce 60 days
  // ago but has zero in the selected last_7d window still has
  // ecommerce.windowActive === false, so we hide the section + KPI tiles.
  const ecommerce = status.report.ecommerce ?? null;
  const ecommerceActive = ecommerce?.windowActive === true;
  const currencyCode = meta.currencyCode ?? null;

  return (
    <div className="space-y-10">
      <KpiGrid
        overview={overview}
        compare={meta.compare}
        propertyId={meta.propertyId}
        ecommerceActive={ecommerceActive}
        currencyCode={currencyCode}
      />
      <div className="flex justify-end">
        <GranularityControl granularity={opts.granularity} onChange={opts.onGranularityChange} />
      </div>
      <TimeSeriesSection
        trend={status.report.trend}
        customConversionsConfigured={overview.customConversions !== undefined}
        showRevenue={showRevenue}
        granularity={opts.granularity}
      />
      {status.report.sourceMix ? (
        <SourceMixSection
          sourceMix={status.report.sourceMix}
          compare={meta.compare}
          granularity={opts.granularity}
        />
      ) : null}
      <DepthSection trend={status.report.trend} granularity={opts.granularity} />
      {/* M13.6 + M14 — InsightsSection moved to the top level so it
          fires /api/insights in parallel with /api/report. See the
          ReportOverview render tree above. */}
      <ReportSection
        eyebrow="What to do next"
        title={
          <>
            Important <em className="font-serif text-purple-700">actions</em> to consider
          </>
        }
      >
        <ExecutiveSummary recommendations={recommendations} propertyId={meta.propertyId} />
        {digestSubscribedForProperty ? null : <DigestNudge />}
      </ReportSection>
      <ReportSection
        id="platforms"
        eyebrow="Platforms"
        title={
          <>
            Where <em className="font-serif text-purple-700">social traffic</em> is coming from
          </>
        }
        description="Per-platform breakdown of every session GA4 attributed to social acquisition over the window. Sortable by sessions, engagement rate, and conversions; the inline bar shows each platform's share of the section total."
      >
        <PlatformLeaderboard
          rows={perPlatform}
          compare={meta.compare}
          showRevenue={showRevenue}
          propertyId={meta.propertyId}
          ecommercePerPlatform={ecommerceActive ? ecommerce!.perPlatform : undefined}
          currencyCode={currencyCode}
        />
      </ReportSection>
      {/* M13.3 — Mobile section. Mounted directly after the platform
          leaderboard so the social cohort's device split sits beside its
          platform split. The block is optional on the schema for legacy
          envelopes; /api/report always populates it. */}
      {status.report.mobile ? (
        <MobileSection
          mobile={status.report.mobile}
          granularity={opts.granularity}
          showRevenue={showRevenue}
          currencyCode={status.report.meta.currencyCode}
        />
      ) : null}
      <ReportSection
        id="landing-pages"
        eyebrow="Landing pages"
        title={
          <>
            Pages winning the <em className="font-serif text-purple-700">click</em>
          </>
        }
        description="Top entry pages for the social cohort, capped at the busiest 250. Filter by platform or search a path; the export button mirrors whatever's currently filtered so the CSV matches what's on screen."
      >
        <LandingPageTable
          rows={landingPages}
          propertyLabel={propertyLabel}
          periodLabel={presetPeriodLabel(meta.range)}
          propertyId={meta.propertyId}
        />
      </ReportSection>
      {/* M13.4 — Ecommerce section. Mounted AFTER LandingPageTable and
          BEFORE CtaPlacement per the plan. Renders only when the probe
          passed AND the selected window has activity. */}
      {ecommerceActive && ecommerce ? (
        <EcommerceSection
          ecommerce={ecommerce}
          currencyCode={currencyCode}
          granularity={opts.granularity}
          compareActive={meta.compare !== "none"}
        />
      ) : null}
      <CtaPlacement
        context={{
          route: "/app/report",
          signals: {
            utmScore: status.report.utmHealth.score,
            socialSessions: overview.sessions,
          },
        }}
      />

      <ReportSection
        id="geography"
        eyebrow="Geography"
        title={
          <>
            Where in the <em className="font-serif text-purple-700">world</em>
          </>
        }
        description="Country-level distribution of social sessions over the window. The choropleth shades by session volume; the country breakdown table below carries the same numbers with conversion counts so you can spot where social engagement actually converts."
      >
        {perCountry.length === 0 ? (
          <CountryTable rows={perCountry} propertyId={meta.propertyId} />
        ) : (
          <div className="space-y-4">
            <Suspense fallback={<ChoroplethSkeleton />}>
              <WorldChoropleth rows={perCountry} />
            </Suspense>
            <details className="group rounded-xl border border-neutral-200 bg-white">
              <summary className="flex cursor-pointer select-none list-none items-center justify-between px-4 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50 [&::-webkit-details-marker]:hidden">
                <span>Country breakdown</span>
                <i
                  className="fa-solid fa-chevron-down text-xs transition-transform group-open:rotate-180"
                  aria-hidden="true"
                />
              </summary>
              <div className="border-t border-neutral-200 p-4">
                <CountryTable rows={perCountry} propertyId={meta.propertyId} />
              </div>
            </details>
          </div>
        )}
      </ReportSection>
    </div>
  );
}

interface ReportSectionProps {
  id?: string;
  eyebrow: string;
  title: ReactNode;
  /** Optional one-paragraph explainer that sits below the title.
   *  Forwarded to `<SectionHeader description>` — same treatment as
   *  the section-component variants. */
  description?: ReactNode;
  children: ReactNode;
}

function ReportSection({ id, eyebrow, title, description, children }: ReportSectionProps) {
  return (
    <section id={id} className="scroll-mt-24 space-y-4">
      <SectionHeader eyebrow={eyebrow} title={title} description={description} />
      {children}
    </section>
  );
}

interface KpiGridProps {
  loading?: boolean;
  overview?: ReportPayload["overview"];
  compare?: ReportCompare;
  propertyId?: string;
  /** M13.4 — when true, the grid renders the Transactions + AOV tiles
   *  alongside the existing tiles. Caller gates this on probe-pass +
   *  windowActive so the tiles only appear when there's data behind
   *  them. */
  ecommerceActive?: boolean;
  /** ISO 4217 from `meta.currencyCode`. Drives currency formatting on
   *  the AOV tile (the existing Revenue tile keeps its USD-anchored
   *  formatter for backward compat). Null = plain numbers. */
  currencyCode?: string | null;
}

function KpiGrid({
  loading,
  overview,
  compare,
  propertyId,
  ecommerceActive,
  currencyCode,
}: KpiGridProps) {
  if (loading || !overview) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard state="loading" label="Sessions" />
        <MetricCard state="loading" label="Engagement rate" />
        <MetricCard state="loading" label="Pages / session" />
        <MetricCard state="loading" label="Conversions" />
      </div>
    );
  }

  const showRevenue = overview.totalRevenue > 0;
  const hasCustomConversions = overview.customConversions !== undefined;
  const compareActive = compare !== "none";
  // M13.4 — only render Transactions + AOV when ecommerceActive AND the
  // overview actually carries the fields (defensive: aggregator stamps
  // them iff windowActive but the schema marks them optional).
  const showEcommerceTiles =
    ecommerceActive === true &&
    overview.transactions !== undefined &&
    overview.averageOrderValue !== undefined;

  // Compute tile count for the grid — Sessions + Engagement + Pages /
  // session + Conversions are always rendered; Custom conversions,
  // Revenue, Transactions + AOV are conditional. Total ranges from 4
  // (baseline) to 8 (everything on).
  const tileCount =
    4 + (hasCustomConversions ? 1 : 0) + (showRevenue ? 1 : 0) + (showEcommerceTiles ? 2 : 0);
  const gridClass =
    tileCount >= 7
      ? "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7"
      : tileCount === 6
        ? "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
        : tileCount === 5
          ? "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
          : "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4";

  return (
    <div className={gridClass}>
      <MetricCard
        state="data"
        label="Sessions"
        value={overview.sessions}
        delta={compareActive ? overview.deltas.sessions : null}
        info={sessionsInfo}
      />
      <MetricCard
        state="data"
        label="Engagement rate"
        value={overview.engagementRate}
        delta={
          compareActive
            ? engagementRatePpDelta(overview.engagementRate, overview.deltas.engagementRate ?? null)
            : null
        }
        deltaUnit="pp"
        format={percent}
        info={engagementRateInfo}
      />
      <MetricCard
        state="data"
        label="Pages / session"
        value={overview.pagesPerSession}
        delta={compareActive ? (overview.deltas.pagesPerSession ?? null) : null}
        format={pagesPerSession}
        info={pagesPerSessionInfo}
      />
      <MetricCard
        state="data"
        label="Conversions"
        value={overview.keyEvents}
        delta={compareActive ? overview.deltas.keyEvents : null}
        info={keyEventsInfo(propertyId)}
      />
      {hasCustomConversions ? (
        <MetricCard
          state="data"
          label="Custom conversions"
          value={overview.customConversions ?? 0}
          delta={compareActive ? (overview.deltas.customConversions ?? null) : null}
          info={customConversionsInfo}
        />
      ) : null}
      {showRevenue ? (
        <MetricCard
          state="data"
          label="Revenue"
          value={overview.totalRevenue}
          delta={compareActive ? overview.deltas.totalRevenue : null}
          format={currency}
          info={revenueInfo}
        />
      ) : null}
      {showEcommerceTiles ? (
        <>
          <MetricCard
            state="data"
            label="Transactions"
            value={overview.transactions ?? 0}
            delta={compareActive ? (overview.deltas.transactions ?? null) : null}
            info={transactionsInfo}
          />
          <MetricCard
            state="data"
            label="Avg order value"
            value={overview.averageOrderValue ?? 0}
            delta={compareActive ? (overview.deltas.averageOrderValue ?? null) : null}
            // Currency formatter built off the resolved currencyCode so
            // AUD properties render "$12" as A$12, EUR as €12, etc.
            // Falls back to plain locale-formatted numbers when null.
            format={(n) => formatAOV(n, currencyCode)}
            info={averageOrderValueInfo}
          />
        </>
      ) : null}
    </div>
  );
}

function formatAOV(n: number, currencyCode: string | null | undefined): string {
  if (currencyCode && /^[A-Z]{3}$/.test(currencyCode)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode,
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      // unknown ISO 4217 — fall through
    }
  }
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

function percent(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

// M13.2 — KPI tile formatter for "Pages / session". Two decimals reads
// like the natural ratio (1.32 pages); 1 decimal would round 1.34 to
// 1.3 and lose useful signal at the dashboard's typical scale (1..3).
function pagesPerSession(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2);
}

function currency(n: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

// Convert the API's relative-% engagement-rate delta into a percentage-point
// delta for the KPI tile. The payload stores `deltas.engagementRate` as a
// relative decimal (0.453 = +45.3% vs previous period); the UI should show
// an absolute pp shift (73.1% current - ~50.3% previous = +22.8 pp). Derives
// the previous value from `current / (1 + relativeDelta)` so we don't need
// a second round-trip. Returns a decimal pp value suitable for
// `MetricCard.deltaUnit="pp"` (DeltaPill multiplies by 100 + appends "pp").
// Returns null when the relative delta is null, the denominator would be
// zero, or the current rate is non-finite.
function engagementRatePpDelta(currentRate: number, relativeDelta: number | null): number | null {
  if (relativeDelta === null || !Number.isFinite(relativeDelta)) return null;
  if (!Number.isFinite(currentRate)) return null;
  const denom = 1 + relativeDelta;
  if (denom === 0) return null;
  const previousRate = currentRate / denom;
  return currentRate - previousRate;
}

// Kept as a compact label for downstream consumers (eg. LandingPageTable
// copy) that prefer a preset-style phrase over a full date window. Custom
// ranges collapse to "Custom range" — the date chip next to the section
// title already carries the exact window.
function presetPeriodLabel(range: ReportPayload["meta"]["range"]): string {
  if (range === "last_7d") return "Last 7 days · social acquisition only";
  if (range === "last_90d") return "Last 90 days · social acquisition only";
  if (range === "custom") return "Custom range · social acquisition only";
  return "Last 28 days · social acquisition only";
}

function DigestNudge() {
  return (
    <aside
      aria-label="Weekly digest opt-in"
      className="mt-4 flex flex-col gap-3 rounded-xl border border-purple-200 bg-purple-50/50 p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0 text-pretty">
        <p className="font-display text-sm font-semibold text-neutral-950">
          Want this report in your inbox every Monday?
        </p>
        <p className="mt-1 text-sm text-neutral-700">
          Turn on the weekly digest from Settings to get a 7-day summary sent to you automatically.
        </p>
        <p className="mt-1 text-sm text-neutral-700">
          You can unsubscribe at any time — one click in the email footer or toggle it off here.
        </p>
      </div>
      <Button asChild size="sm" className="shrink-0">
        <Link to="/app/settings#weekly-digest">Enable digest →</Link>
      </Button>
    </aside>
  );
}
